import { useCallback, useEffect, useState } from 'react';

const getBackendHost = () =>
  (
    import.meta as unknown as {
      env?: { VITE_APP_DJANGO_BACKEND_HOST?: string };
    }
  ).env?.VITE_APP_DJANGO_BACKEND_HOST ?? '';

export const useUploadedLorPreview = (
  matterId: string,
  coverageId: string,
  enabled: boolean,
) => {
  const backendHost = getBackendHost();
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const revokeUrl = useCallback((value: string | null) => {
    if (value) {
      URL.revokeObjectURL(value);
    }
  }, []);

  const request = useCallback(async () => {
    if (!enabled || !matterId || !coverageId) {
      setUrl((prev) => {
        revokeUrl(prev);
        return null;
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${backendHost}/api/matters/${matterId}/coverages/${coverageId}/upload-lor/preview/`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load uploaded LoR preview');
      }
      const blob = await response.blob();
      const nextUrl = URL.createObjectURL(blob);
      setUrl((prev) => {
        revokeUrl(prev);
        return nextUrl;
      });
    } catch (err) {
      setError(err);
      setUrl((prev) => {
        revokeUrl(prev);
        return null;
      });
    } finally {
      setIsLoading(false);
    }
  }, [backendHost, coverageId, enabled, matterId, revokeUrl]);

  useEffect(() => {
    if (enabled) {
      request();
    } else {
      setUrl((prev) => {
        revokeUrl(prev);
        return null;
      });
    }

    return () => {
      setUrl((prev) => {
        revokeUrl(prev);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, request]);

  return {
    url,
    isLoading,
    error,
    request,
  } as const;
};
